import { useContext } from "react";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import {
  firestoreCreateCategory,
  firestoreCreateCategoryGroup,
} from "../firebaseio/firestoreIo";
import { RyeButton } from "../rye/RyeButton";
import { RyeEmptyState } from "../rye/RyeEmptyState";
import { UserContext } from "../firebaseio/UserContext";
import { buildClasses } from "../utils/buildClasses";
import {
  RECOMMENDED_CATEGORIES,
  RecommendedCategories,
  RecommendedCategoryGroups,
} from "./recommendedCategories";
import dayjs from "dayjs";
import { FocusCategoryGroupIndexContext } from "./CategoriesScreen";

export function CategoryStarterSetup(): JSX.Element | null {
  const categoryGroups = useContext(CategoryGroupsContext);
  const categories = useContext(CategoriesContext);
  const user = useContext(UserContext);
  const [, setFocusCategoryGroupIndex] = useContext(
    FocusCategoryGroupIndexContext,
  );

  async function createRecommendedCategories() {
    const recommendedGroups = RecommendedCategoryGroups();
    const groupIds: string[] = [];
    for (const group of recommendedGroups) {
      const groupId = await firestoreCreateCategoryGroup(user.uid, group);
      groupIds.push(groupId);
    }

    const recommendedCategories = RecommendedCategories(groupIds);
    for (const category of recommendedCategories) {
      firestoreCreateCategory(user.uid, category); // don't await to make the UI more responsive
    }
  }

  if (categories.size > 0 || categoryGroups.size > 0) return null;

  return (
    // Room below the card, which is taller than the pane and scrolls.
    <div className={buildClasses("pb-12")}>
      <RyeEmptyState
        size="compact"
        wide
        icon="category"
        title="Create your categories"
        description="Categories allow you to organize your transactions into meaningful buckets that make it easier to track and forcast your income, expenses, and savings."
        actions={
          <>
            <RyeButton
              text="Start with the template below"
              variant="filled"
              vibe="primary"
              onClick={createRecommendedCategories}
            />
            <RyeButton
              text="Start from scratch"
              variant="outlined"
              vibe="subdued"
              onClick={() => {
                firestoreCreateCategoryGroup(user.uid, {
                  name: "New Group",
                  created_timestamp_secs: dayjs().unix(),
                  order_position: categoryGroups.size,
                });
                setFocusCategoryGroupIndex(categoryGroups.size);
              }}
            />
          </>
        }
      >
        {/* Preview of recommended categories */}
        <div
          className={buildClasses(
            "mt-4",
            "w-full",
            "flex",
            "flex-col",
            "gap-4",
            "border-t",
            "border-on-surface-200",
            "pt-6",
            "text-left",
          )}
        >
          {RECOMMENDED_CATEGORIES.map((group) => (
            <div key={group.group_name}>
              <p
                className={buildClasses(
                  "mb-1",
                  "text-sm",
                  "font-medium",
                  "text-on-surface-700",
                )}
              >
                {group.group_name}
              </p>
              <div className={buildClasses("flex", "flex-col", "gap-0.5")}>
                {group.categories.map((cat) => (
                  <span
                    key={cat.name}
                    className={buildClasses(
                      "pl-4",
                      "text-sm",
                      "text-on-surface-600",
                    )}
                  >
                    {cat.emoji} {cat.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </RyeEmptyState>
    </div>
  );
}
